import type {
  GetApiV1RosterCompetitionsTeamIdParams,
  GetApiV1RosterFunctionariesTeamIdParams,
  GetApiV1RosterTeamIdEditFunctionariesParams,
  GetApiV1RosterTeamIdEditPlayersInvalidParams,
  GetApiV1RosterTeamIdEditPlayersParams,
  GetApiV1RosterTeamIdEditPlayersValidParams,
  GetApiV1TransferUserUserIdRosterParams,
} from '@1webit/cvs-api-scheme/models';
import type { GetApiV1RosterPlayersTeamIdParams } from '@1webit/cvs-api-scheme/models/getApiV1RosterPlayersTeamIdParams';
import { queryOptions } from '@tanstack/react-query';

import { DEFAULT_TABLE_PAGE_SIZE, MAX_API_LIMIT } from '@/constants';
import type { BaseTableParams } from '@/utils';
import { getResponse, paginationToApiFormat, queryParams } from '@/utils';

// my rosters
export const getApiV1RosterMyRosters = (params: BaseTableParams) => queryOptions({
  queryKey: ['getApiV1RosterMyRosters', params],
  queryFn: () => getResponse('getApiV1RosterMyRosters', paginationToApiFormat(params)),
});

export const getApiV1RosterMyRostersOld = (params: BaseTableParams) => queryOptions({
  queryKey: ['getApiV1RosterMyRostersOld', params],
  queryFn: () => getResponse('getApiV1RosterMyRostersOld', paginationToApiFormat(params)),
});

export const getApiV1RosterMatchesToday = queryOptions({
  queryKey: ['getApiV1RosterMatchesToday'],
  queryFn: async () => {
    const res = await getResponse('getApiV1RosterMatchesToday');
    return { responseData: res };
  },
});
// managed
export const getApiV1RosterManaged = queryOptions({
  queryKey: ['getApiV1RosterManaged'],
  queryFn: () => getResponse('getApiV1RosterManaged'),
});

/**
 * Get roster players
 * @param teamId - The team ID
 * @param params - The parameters to pass to the API
 */
export const getRosterPlayers = (teamId: number, params: GetApiV1RosterPlayersTeamIdParams) => queryOptions({
  queryKey: ['getApiV1RosterPlayersTeamId', teamId, params],
  queryFn: () => getResponse('getApiV1RosterPlayersTeamId', teamId, params),
});

/**
 * Get roster functionaries
 * @param teamId - The team ID
 * @param params - The parameters to pass to the API
 */
export const getRosterFunctionaries = (teamId: number, params: GetApiV1RosterFunctionariesTeamIdParams) => queryOptions({
  queryKey: ['getApiV1RosterFunctionariesTeamId', teamId, params],
  queryFn: () => getResponse('getApiV1RosterFunctionariesTeamId', teamId, params),
});

/**
 * Get roster versions data
 * @param teamId - The team ID
 */
export const getRosterVersionsData = (teamId: number) => queryOptions({
  queryKey: ['getApiV1RosterVersionsTeamId', teamId],
  queryFn: () => getResponse('getApiV1RosterVersionsTeamId', teamId),
});

// statistics
export const getApiV1RosterStatisticsCategory = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1RosterStatisticsCategory', page, limit, sort],
    queryFn: () => getResponse('getApiV1RosterStatisticsCategory', queryParams({ limit, offset: (page - 1) * limit, sort })),
  });
};
export const getApiV1RosterStatisticsClub = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1RosterStatisticsClub', page, limit, sort],
    queryFn: () => getResponse('getApiV1RosterStatisticsClub', queryParams({ limit, offset: (page - 1) * limit, sort })),
  });
};
export const getApiV1RosterStatisticsLetter = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1RosterStatisticsLetter', page, limit, sort],
    queryFn: () => getResponse('getApiV1RosterStatisticsLetter', queryParams({ limit, offset: (page - 1) * limit, sort })),
  });
};
export const getApiV1RosterStatisticsCompetition = (page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1RosterStatisticsCompetition', page, limit, sort],
    queryFn: () => getResponse('getApiV1RosterStatisticsCompetition', queryParams({ limit, offset: (page - 1) * limit, sort })),
  });
};
export const getApiV1RosterStatisticsRegion = () => {
  return queryOptions({
    queryKey: ['getApiV1RosterStatisticsRegion'],
    queryFn: () => getResponse('getApiV1RosterStatisticsRegion'),
  });
};
export const getApiV1RosterStatisticsComplete = () => {
  return queryOptions({
    queryKey: ['getApiV1RosterStatisticsComplete'],
    queryFn: () => getResponse('getApiV1RosterStatisticsComplete'),
  });
};

interface GetRosterByUserParams extends Omit<GetApiV1TransferUserUserIdRosterParams, 'offset'> {
  page: number;
  limit: number;
}

/**
 * Get roster by user
 * @param userId - The user ID
 * @param params - The parameters to pass to the API
 */
export const getRosterByUserQueryOptions = (userId: number, params?: GetRosterByUserParams) => queryOptions({
  queryKey: ['getApiV1TransferUserUserIdRoster', userId, params],
  queryFn: () => getResponse('getApiV1TransferUserUserIdRoster', userId, {
    ...params,
    offset: (params !== undefined && params.page !== undefined && params.limit !== undefined) ? (params.page - 1) * params.limit : 0,
  }),
});

// competition
export const getApiV1RosterCompetitionCompetitionIdDetail = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1RosterCompetitionCompetitionIdDetail', id],
    queryFn: () => getResponse('getApiV1RosterCompetitionCompetitionIdDetail', id, {
      limit: MAX_API_LIMIT,
    }),
  });
};

// user-rights
export const getApiV1RosterUserRights = (teamId?: number, competitionId?: string) => {
  const params = queryParams({ teamId, competitionId });

  return queryOptions({
    queryKey: ['getApiV1RosterUserRights', params],
    queryFn: () => getResponse('getApiV1RosterUserRights', params),
  });
};

export const getApiV1RosterCompetitionsTeamId = (teamId: number, params?: GetApiV1RosterCompetitionsTeamIdParams) => queryOptions({
  queryKey: ['getApiV1RosterCompetitionsTeamId', teamId, params],
  queryFn: () => getResponse('getApiV1RosterCompetitionsTeamId', teamId, params),
});

export const getApiV1RosterTeamIdEditPlayers = (teamId: number, params?: GetApiV1RosterTeamIdEditPlayersParams) => queryOptions({
  queryKey: ['getApiV1RosterTeamIdEditPlayers', teamId, params],
  queryFn: () => getResponse('getApiV1RosterTeamIdEditPlayers', teamId, params),
});

export const getApiV1RosterTeamIdEditFunctionaries = (teamId: number, params?: GetApiV1RosterTeamIdEditFunctionariesParams) => queryOptions({
  queryKey: ['getApiV1RosterTeamIdEditFunctionaries', teamId, params],
  queryFn: () => getResponse('getApiV1RosterTeamIdEditFunctionaries', teamId, params),
});

export const getApiV1RosterTeamIdEditPlayersValid = (teamId: number, params?: GetApiV1RosterTeamIdEditPlayersValidParams) => queryOptions({
  queryKey: ['getApiV1RosterTeamIdEditPlayersValid', teamId, params],
  queryFn: () => getResponse('getApiV1RosterTeamIdEditPlayersValid', teamId, params),
});

export const getApiV1RosterTeamIdEditPlayersInvalid = (teamId: number, params?: GetApiV1RosterTeamIdEditPlayersInvalidParams) => queryOptions({
  queryKey: ['getApiV1RosterTeamIdEditPlayersInvalid', teamId, params],
  queryFn: () => getResponse('getApiV1RosterTeamIdEditPlayersInvalid', teamId, params),
});
